import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    SxProps,
    DialogActions,
    DialogContent,
    DialogContentText,
    TextField,
    InputAdornment,
    IconButton,
    Stack,
    Box,
    DialogTitle,
} from '@mui/material';
import Logo from '../../../Logo/Logo';
import { vars } from '../../../../../Theme';
import Icon from '@mdi/react';
import { mdiEyeOutline, mdiEyeOffOutline, mdiAlertCircle } from '@mdi/js';
import CtaButton from '../../_parts/CtaButton';
import {
    getDataForInitialReleaseDialog,
    getDataForMainDialog,
    postValidatePassword,
    resetWasPasswordRequestSuccessful,
} from '../../../../../redux/project/sliceProjectWorkflow';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IAppReduxStore } from '../../../../../redux/store';
import Message from '../../_parts/Message';

/** Styles for Dialog's component. */
const dialogSx: SxProps = {
    '& .MuiDialog-paper': {
        width: '90%',
        height: '80%',
        minWidth: '350px',
        maxWidth: '560px',
        maxHeight: '560px',
        padding: 6,
        pt: 3,
        pb: 4.5,
    },
};

const passwordInputDiscSx = {
    '& .MuiInputBase-input': {
        WebkitTextSecurity: 'disc',
    },
};

/**
 * Interface that describes WorkflowPasswordDialog's props.
 */
interface IWorkflowPasswordDialogProps extends React.PropsWithChildren {
    open: boolean;
    onClose?: () => void;
}

export default ({ open, onClose }: IWorkflowPasswordDialogProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'passwordDialog' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get the replicaId from the react router. */
    const { replicaId, areaName } = useParams();

    const wasPasswordRequestSuccessful = useSelector(
        (_store: IAppReduxStore) => _store.projectWorkflow.wasPasswordRequestSuccessful,
    );
    const documentIds = useSelector(
        (_store: IAppReduxStore) => _store.projectSelectedDocuments.storage.documentIds,
    );

    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showError, setShowError] = useState(false);

    const handlePasswordChange = event => {
        setPassword(event.target.value);
        setShowError(false);
    };

    const handleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        dispatch(resetWasPasswordRequestSuccessful());
        if (wasPasswordRequestSuccessful) {
            // Password is valid, close the dialog
            handleClose();
            areaName === 'import'
                ? dispatch(
                      getDataForInitialReleaseDialog({
                          replicaId,
                          documentIds,
                      }),
                  )
                : dispatch(getDataForMainDialog({ replicaId, documentIds }));
        } else if (wasPasswordRequestSuccessful === false) {
            setShowError(true);
        }
    }, [wasPasswordRequestSuccessful]);

    const handleConfirm = () => {
        dispatch(postValidatePassword({ replicaId, password }));
    };

    // Close the dialog without performing any action
    const handleClose = () => {
        setPassword('');
        setShowPassword(false);
        setShowError(false);
        onClose();
    };

    return (
        <Dialog open={open} sx={dialogSx}>
            <Stack direction='row'>
                <Logo darkMode boxSx={{ pl: 0 }} />
            </Stack>

            <DialogContent sx={{ px: 0, pt: 1, overflowX: 'hidden'}}>
                {showError && (
                    <Message
                        text={t('invalidPassword')}
                        color={vars.errorDialogRed}
                        icon={mdiAlertCircle}
                    />
                )}
                <Box sx={{ position: 'absolute', top: '168px', width: '464px' }}>
                    <DialogTitle sx={{ px: 0, pt: 0 }}>{t('enterPassword')}</DialogTitle>
                    <DialogContentText sx={{ pb: 2, color: vars.font }}>
                        {t('enterPasswordMessage')}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin='dense'
                        size='small'
                        error={showError}
                        color={showError ? 'error' : 'primary'}
                        label={t('password')}
                        type={'text'}
                        fullWidth
                        value={password}
                        onChange={handlePasswordChange}
                        onKeyUp={e => e.key === 'Enter' && handleConfirm()}
                        sx={showPassword ? {} : passwordInputDiscSx}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        {password !== '' && (
                                            <IconButton onClick={handleShowPassword} edge='end'>
                                                {showPassword ? (
                                                    <Icon path={mdiEyeOutline} size={1} />
                                                ) : (
                                                    <Icon path={mdiEyeOffOutline} size={1} />
                                                )}
                                            </IconButton>
                                        )}
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 0 }}>
                <CtaButton variant='text' text={t('cancel')} onClick={handleClose} />
                <CtaButton variant='contained' text={t('confirm')} onClick={handleConfirm} />
            </DialogActions>
        </Dialog>
    );
};
